import React from "react";
import { Box } from "@mui/material";
import styles from "./PrivacyPolicy.module.css";

const PrivacyPolicy = () => {
  return (
    <Box className="RegisterDiv" sx={{ justifyContent: "flex-start", py: 2 }}>
      <div className={styles.wrap}>
        <article className={styles.panel}>
          <header className={styles.header}>
            <h1 className={styles.title}>Privacy Policy</h1>
            <p className={styles.meta}>Last updated: April 30, 2026</p>
            <p className={styles.meta}>Effective date: April 30, 2026</p>
          </header>

          <p className={styles.intro}>
            MyNetGoals (&quot;we,&quot; &quot;our,&quot; &quot;us&quot;) values
            your privacy. This Privacy Policy explains how we collect, use,
            disclose, and safeguard your information when you use our mobile
            application, MyNetGoals (the &quot;App&quot;). By using the App,
            you agree to the terms of this Privacy Policy.
          </p>

          <h2 className={styles.sectionTitle}>1. Introduction</h2>
          <p className={styles.paragraph}>
            Welcome to MyNetGoals. Your privacy is important to us. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your
            information when you use our mobile application.
          </p>
          <p className={styles.paragraph}>
            By using the app, you agree to the collection and use of information
            in accordance with this policy.
          </p>

          <h2 className={styles.sectionTitle}>2. Information We Collect</h2>
          <p className={styles.paragraph}>
            We may collect the following types of information:
          </p>

          <h3 className={styles.subheading}>a. Personal Information</h3>
          <ul className={styles.list}>
            <li>Name</li>
            <li>Email address</li>
            <li>Username</li>
            <li>Profile photo (if uploaded)</li>
            <li>Account credentials</li>
          </ul>

          <h3 className={styles.subheading}>b. User-Generated Content</h3>
          <ul className={styles.list}>
            <li>
              Videos uploaded (e.g., goals, tournament clips, league or club
              content)
            </li>
            <li>Comments, votes, and leaderboard participation</li>
          </ul>

          <h3 className={styles.subheading}>c. Usage Data</h3>
          <ul className={styles.list}>
            <li>App interactions (e.g., uploads, votes, views)</li>
            <li>Device information (device type, OS version)</li>
            <li>IP address and general location (non-precise)</li>
          </ul>

          <h3 className={styles.subheading}>d. Optional Information</h3>
          <p className={styles.paragraph}>
            Any additional details you provide in your profile.
          </p>

          <h2 className={styles.sectionTitle}>3. How We Use Your Information</h2>
          <p className={styles.paragraph}>We use your data to:</p>
          <ul className={styles.list}>
            <li>Create and manage user accounts</li>
            <li>Enable video uploads and sharing</li>
            <li>Maintain leaderboards and voting systems</li>
            <li>Improve app performance and user experience</li>
            <li>Communicate updates, notifications, or support messages</li>
            <li>Detect and prevent fraud or misuse</li>
          </ul>
          <p className={styles.paragraph}>
            We do not sell, rent, or trade your personal information to third
            parties.
          </p>

          <h2 className={styles.sectionTitle}>4. Content Sharing &amp; Visibility</h2>
          <ul className={styles.list}>
            <li>
              Videos and content uploaded by users may be publicly visible within
              the app.
            </li>
            <li>
              Usernames and profile information may be displayed alongside
              content.
            </li>
            <li>Voting and leaderboard rankings are visible to other users.</li>
          </ul>

          <h2 className={styles.sectionTitle}>5. Data Storage and Security</h2>
          <p className={styles.paragraph}>
            We implement reasonable security measures to protect your information.
            However, no method of transmission over the internet is 100% secure.
          </p>
          <p className={styles.paragraph}>
            We store your data on secure servers and take steps to prevent
            unauthorized access, disclosure, or misuse.
          </p>

          <h2 className={styles.sectionTitle}>6. Third-Party Services</h2>
          <p className={styles.paragraph}>We may use third-party services for:</p>
          <ul className={styles.list}>
            <li>Cloud storage (e.g., video hosting)</li>
            <li>Analytics</li>
            <li>Authentication</li>
          </ul>
          <p className={styles.paragraph}>
            These services may collect and process data according to their own
            privacy policies.
          </p>

          <h2 className={styles.sectionTitle}>7. Children&apos;s Privacy</h2>
          <p className={styles.paragraph}>
            Our app is not intended for children under the age of 13 (or
            applicable age in your region). We do not knowingly collect personal
            data from children.
          </p>
          <p className={styles.paragraph}>
            If we become aware of such data, we will delete it promptly.
          </p>

          <h2 className={styles.sectionTitle}>8. Your Rights</h2>
          <p className={styles.paragraph}>
            Depending on your location, you may have the right to:
          </p>
          <ul className={styles.list}>
            <li>Access your personal data</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your account and data</li>
            <li>Withdraw consent</li>
          </ul>
          <p className={styles.paragraph}>
            To exercise these rights, contact us at:{" "}
            <a
              className={styles.link}
              href="mailto:mynetgoals01@gmail.com"
            >
              mynetgoals01@gmail.com
            </a>
          </p>

          <h2 className={styles.sectionTitle}>9. Data Retention</h2>
          <p className={styles.paragraph}>
            We retain your data only as long as necessary for:
          </p>
          <ul className={styles.list}>
            <li>Providing services</li>
            <li>Legal compliance</li>
            <li>Resolving disputes</li>
          </ul>
          <p className={styles.paragraph}>
            You may request deletion of your account at any time.
          </p>

          <h2 className={styles.sectionTitle}>
            10. Changes to This Privacy Policy
          </h2>
          <p className={styles.paragraph}>
            We may update this Privacy Policy from time to time. Changes will be
            posted within the app or on our website with an updated effective
            date. Continued use of the App after changes are posted constitutes
            acceptance of the revised policy.
          </p>

          <h2 className={styles.sectionTitle}>11. Contact Us</h2>
          <p className={styles.paragraph}>
            If you have any questions about this Privacy Policy, please contact
            us:
          </p>
          <p className={styles.paragraph}>
            Email:{" "}
            <a
              className={styles.link}
              href="mailto:mynetgoals01@gmail.com"
            >
              mynetgoals01@gmail.com
            </a>
            <br />
            Company name: MyNetGoals
          </p>

          <h2 className={styles.sectionTitle}>12. Consent</h2>
          <p className={styles.paragraph}>
            By using MyNetGoals, you consent to this Privacy Policy.
          </p>

          {/* <p className={styles.footerNote}>
            © 2026 MyNetGoals. All rights reserved.
          </p> */}
        </article>
      </div>
    </Box>
  );
};

export default PrivacyPolicy;
